import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_URL } from '../config';
import { useAuth } from './AuthContext';
import { getUserId } from '../auth';

interface SocketContextType {
  orderUpdateCount: number;
  credentialUpdateCount: number;
}

const SocketContext = createContext<SocketContextType>({
  orderUpdateCount: 0,
  credentialUpdateCount: 0,
});

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const { isLoggedIn, handleBanned } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [orderUpdateCount, setOrderUpdateCount] = useState(0);
  const [credentialUpdateCount, setCredentialUpdateCount] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    const socket = io(API_URL, { transports: ['websocket'], reconnectionDelay: 3000 });
    socketRef.current = socket;

    socket.on('orderStatusUpdated', () => {
      setOrderUpdateCount(n => n + 1);
    });

    socket.on('credentialUpdate', () => {
      setCredentialUpdateCount(n => n + 1);
    });

    socket.on('userStatusChanged', async (payload: { userId: string; status: string }) => {
      if (!payload || (payload.status !== 'banned' && payload.status !== 'suspended')) return;
      const myId = await getUserId();
      if (myId && payload.userId === myId) {
        handleBanned();
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [isLoggedIn, handleBanned]);

  return (
    <SocketContext.Provider value={{ orderUpdateCount, credentialUpdateCount }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => useContext(SocketContext);
